import { UserDetailType } from "@/types/index";
import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import _ from "lodash";

import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from "next";

import djs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import LocalisedFormat from "dayjs/plugin/localizedFormat";
import { AxiosCargo, EStatusCode, GameItem } from "./declarations";
import qs from "qs";
import { redirect } from "@/i18n/routing";
import { getLocale } from "next-intl/server";
import { Dispatch, SetStateAction } from "react";

djs.extend(tz);
djs.extend(utc);
djs.extend(LocalisedFormat);

export const UCFirst = (input: string) => {
  return input.charAt(0).toUpperCase() + input.slice(1);
};

export const GetStatusTextColour = (status: string) => {
  switch (status) {
    case "in-progress":
    case "pending":
      return "text-gray-500";
    case "approved":
      return "text-[#258966]";
    case "rejected":
      return "text-red-500";
  }
};

export const GetStatusIcon = (status: string) => {
  switch (status) {
    case "in-progress":
    case "pending":
      return "icon-icon14";
    case "approved":
      return "icon-icon11";
    case "rejected":
      return "icon-icon12";
  }
};

export const ReducedString = (input: string, length: number) => {
  return input.length > length ? input.substring(0, length) + "..." : input;
};

export const checkUserDetail = (userDetail: string) => {
  const phoneRegex = /^\+?[0-9]+$/;
  if (phoneRegex.test(userDetail)) {
    return UserDetailType.PHONE;
  } else {
    return UserDetailType.USERNAME;
  }
};

// Define the type for the custom getServerSideProps function
type GetServerSidePropsFunc = (context: GetServerSidePropsContext) => Promise<GetServerSidePropsResult<any>>;

// Define the withAuth function
export const withAuth = (getServerSidePropsFunc?: GetServerSidePropsFunc): GetServerSideProps => {
  return async (context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<any>> => {
    const { req } = context;
    const token = req.cookies.token; // Access the cookie from the request

    // Check if token exists
    if (!token) {
      return {
        redirect: {
          destination: "/login", // Redirect to login if not authenticated
          permanent: false,
        },
      };
    }

    // If authenticated, call the custom `getServerSidePropsFunc`
    if (getServerSidePropsFunc) {
      return await getServerSidePropsFunc(context); // Await the custom function
    }

    return {
      props: {}, // Return empty props if no additional logic
    };
  };
};

export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
};

export const GetProviderImgPath = (provider_name: string) => {
  return _.concat("/providers/", provider_name, ".png").join("");
};

export const TransformProviderLinkByType = (type: "provider" | "category", category: string, provider: string) => {
  return type === "provider" ? `/games/by-${type}/${provider}` : `/games/by-${type}/${category}/${provider}`;
};

export const TransformCategoryLinkByType = (type: "provider" | "category", category: string, provider: string) => {
  return type === "category" ? `/games/by-${type}/${category}` : `/games/by-${type}/${provider}/${category}`;
};

export const RenameProvider = (provider: string) => {
  switch (provider) {
    // case "evolutiongaming":
    //   return "evo";
    // case "jili":
    //   return "jilislot";
    // case "go888king":
    //   return "go888king";
    // case "pragmaticplay":
    //   return "nexuspragmatic";
    case "relaxgaming":
      return "relax";
    case "bigtimegaming":
      return "btg";
    default:
      return provider;
  }
};

export const ReverseRenameProvider = (provider: string) => {
  switch (provider) {
    // case "evo":
    //   return "evolutiongaming";
    // case "jilislot":
    //   return "jili";
    // case "go888king":
    //   return "go888king";
    // case "nexuspragmatic":
    //   return "pragmaticplay";
    case "relax":
      return "relaxgaming";
    case "btg":
      return "bigtimegaming";
    default:
      return provider;
  }
};

export const GetProviderFullname = (provider: string) => {
  // Original list of providers
  // [
  //   'Red Tiger',        'NetEnt',
  //   'Evolution Gaming', 'Big Time Gaming',
  //   "Play'n Go",        'Relax Gaming',
  //   'Pragmatic Play',   'Spribe',
  //   'Sap',              'Playtech',
  //   'Nolimit City',     'Aviatrix',
  //   'Wazdan',           '3 Oaks Gaming',
  //   'Gamzix',           'Spinomenal',
  //   'GameBeat',         'RubyPlay',
  //   'Jili',             'Push Gaming'
  // ]
  switch (provider) {
    case "redtiger":
      return "Red Tiger";
    case "netent":
      return "NetEnt";
    case "evo":
      return "Evolution Gaming";
    case "btg":
      return "Big Time Gaming";
    case "playngo":
      return "Play'n Go";
    case "relax":
      return "Relax Gaming";
    case "nexuspgsoft":
      return "PGSoft";
    case "nexuspragmatic":
      return "Pragmatic Play";
    case "spribe":
      return "Spribe";
    case "sap":
      return "Sap";
    case "playtech":
      return "Playtech";
    case "nolimitcity":
      return "Nolimit City";
    case "aviatrix":
      return "Aviatrix";
    case "wazdan":
      return "Wazdan";
    case "3oaksgaming":
      return "3 Oaks Gaming";
    case "gamzix":
      return "Gamzix";
    case "spinomenal":
      return "Spinomenal";
    case "gamebeat":
      return "GameBeat";
    case "rubyplay":
      return "RubyPlay";
    case "jilislot":
      return "Jili";
    case "pushgaming":
      return "Push Gaming";
    case "go888king":
      return "Go888King";
    default:
      return provider;
  }
};

// djs.tz.setDefault("Asia/Kuala_Lumpur");
djs.tz.setDefault("Asia/Dhaka");
export const dayjs = djs;

export const ProviderSorter: (category: string, provider: string) => number = (category: string, provider: string) => {
  switch (category) {
    case "hot_games":
    case "slot":
      if (/^pragmatic(\s|)play*/gi.test(provider)) {
        return 2;
      }

      switch (provider) {
        case "jilislot":
          return 0;
        // case "cpgames":
        //   return 1;
        case "pragmaticplay":
        case "pragmaticplayslot":
        case "nexuspragmatic":
          return 2;
        case "nexuspgsoft":
          return 3;
        case "smartsoftgaming":
          return 4;
        case "jdb":
          return 5;
        case "playngo":
          return 6;
        case "playtech":
          return 7;
        case "netent":
          return 8;
        case "btg":
          return 9;
        case "redtiger":
          return 10;
        case "go888king":
          return 11;
        case "habanero":
          return 12;
        case "nexusjoker":
          return 13;
        default:
          return 1000;
      }
    case "live":
      if (/^pragmatic(\s|)play*/gi.test(provider)) {
        return 2;
      }
      switch (provider) {
        case "evo":
          return 1;
        case "pragmaticplaylive":
          return 2;
        case "aesexy":
          return 3;
        case "ezugi":
          return 4;
        case "playtechlive":
          return 5;
        default:
          return 1000;
      }
    case "crash":
      if (/^pragmatic(\s|)play*/gi.test(provider)) {
        return 2;
      }
      switch (provider) {
        case "jilislot":
          return 0;
        case "spribe":
          return 1;
        case "aviatrix":
          return 2;
        case "smartsoftgaming":
          return 3;
        case "betsolutions":
          return 4;
        default:
          return 1000;
      }
    case "table_games":
      if (/^pragmatic(\s|)play*/gi.test(provider)) {
        return 2;
      }
      switch (provider) {
        case "jilislot":
          return 0;
        case "nexuspragmatic":
          return 1;
        case "spribe":
          return 2;
        case "playngo":
          return 3;
        case "playtech":
          return 4;
        default:
          return 1000;
      }
    case "fishing":
      if (/^pragmatic(\s|)play*/gi.test(provider)) {
        return 2;
      }
      switch (provider) {
        case "jilislot":
          return 0;
        default:
          return 1000;
      }
    default:
      return 1000;
  }
};

// prettier-ignore
export const ranksEXP = [1000, 20000, 50000, 100000, 250000, 500000, 1000000, 1800000, 3000000, 10000000, 20000000, 35000000, 60000000, 90000000, 150000000];

// prettier-ignore
export const ranksName = ["Member ","Bronze 3", "Bronze 2", "Bronze 1", "Silver 3", "Silver 2", "Silver 1", "Gold 3", "Gold 2", "Gold 1", "Platinum 3", "Platinum 2", "Platinum 1", "Diamond 3", "Diamond 2", "Diamond 1"];

export const GetSealRank = (current: number) => {
  const currentRank = ranksEXP.findIndex((exp) => current < exp);
  return {
    currentExp: current,
    rankName: ranksName[currentRank],
    rankIndex: parseInt(ranksName[currentRank].split(" ")[1]),
    nextRankName: ranksName[currentRank + 1],
    nextRankEXP: ranksEXP[currentRank],
    prevRankExp: ranksEXP[currentRank - 1],
    rankBackground: ranksName[currentRank].split(" ")[0].toLowerCase(),
  };
};

export const AppendGameHistoryToLocalStorage = (gameId: string, gameName: string, imageLink: string) => {
  const newGame = `${gameId}|${gameName}|${imageLink}|${new Date().getTime()}`;
  if (localStorage.getItem("recent-games")) {
    const prev = JSON.parse(localStorage.getItem("recent-games") as string);

    localStorage.setItem("recent-games", JSON.stringify([...prev, newGame]));
    localStorage.setItem(
      "recent-games",
      JSON.stringify([...JSON.parse(localStorage.getItem("recent-games") as string)])
    );
  } else {
    localStorage.setItem("recent-games", JSON.stringify([]));
  }
};

export const GetGameHistoryFromLocalStorage = () =>
  _.chain((JSON.parse(localStorage.getItem("recent-games") as string) || []) as string[])
    .sortBy((e) => {
      return parseInt(e.split("|")[3]);
    })
    .reverse()
    .uniqBy((e) => e.split("|")[0])
    .map((e) => {
      const [id, name, imgUrl] = e.split("|");
      return { id, name, imgUrl };
    })
    .value() as GameItem[];

export const toCurl = (config: InternalAxiosRequestConfig<any>) => {
  let params = config.params ? "?" + qs.stringify(config.params, { arrayFormat: "repeat" }) : "";

  let curl = `curl -X ${config.method?.toUpperCase()} '${config.baseURL}${config.url}${params}'`;

  if (config.headers) {
    for (const [key, value] of Object.entries(config.headers)) {
      curl += ` -H '${key}: ${value}'`;
    }
  }

  if (config.data) {
    curl += ` -d '${JSON.stringify(config.data)}'`;
  }

  return curl;
};

// transform axios response and return to client site
export const AxiosSafeCargo = (response: AxiosResponse) => ({
  data: response.data,
  status: response.status,
  statusText: response.statusText,
});

export async function ServerAxiosCargoCustom<T>(data: AxiosCargo<T>) {
  const response = await data;
  switch (response.status) {
    case EStatusCode.SUCCESS:
      return response.data;
    case EStatusCode.INTERNAL_SERVER_ERROR:
      console.log("INTERNAL_SERVER_ERROR: ", response.data);
      return null;
    case EStatusCode.UNAUTHORIZED:
      console.log("UNAUTHORIZED: ", response.data);
      const locale = await getLocale();
      redirect({ locale, href: "/logout" });
      return null;
    default:
      console.log("UNKNOWN_ERROR: ", response.data);
      return null;
  }
}

export const InputCheck = {
  username: {
    format: (value: string) => value.replaceAll(/[^a-zA-Z0-9]/gi, ""),
    isMalformed: (value: string) => /[^a-zA-Z0-9]/gi.test(value),
  },
  number: {
    format: (value: string) => value.replaceAll(/[^0-9]/gi, ""),
    isMalformed: (value: string) => /[^0-9]/gi.test(value),
  }
};

export const RegexNumberInput = (value: string) => value.replaceAll(/[^0-9]/gi, "");
